import { useState } from "react";
import {
  Modal, Box, Typography, TextField, MenuItem, Button, Stack,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { insertMovement } from '../../../../services/CashRegisterService';
import { movementTypes } from '../../../../utils/Enums';

import CurrencySelect from '../../../../components/Currency/CurrencySelect';
import { DEFAULT_CURRENCY } from '../../../../utils/currency';
const SUPPLY_PURCHASE_MOVEMENT_TYPE = 1;
const MOVEMENT_FIELD_NAMES = ["type", "amount", "note"];

const CashRegisterMovementModal = ({ open, onClose, locationId, terminalId, onSuccess }) => {
  const { t } = useTranslation();
  const [type, setType] = useState("");
  const [amount, setAmount] = useState("");
  const [note, setNote] = useState("");
  const [currency, setCurrency] = useState(DEFAULT_CURRENCY);
  const [fieldErrors, setFieldErrors] = useState({});
  const isSupplyPurchase = Number(type) === SUPPLY_PURCHASE_MOVEMENT_TYPE;

  const messagesForField = {
    type: t("cash_movement_type_required"),
    amount: t("cash_movement_amount_invalid"),
    note: t("cash_movement_note_required"),
  };

  const clearFieldError = (field) => {
    setFieldErrors((current) => ({ ...current, [field]: "" }));
  };

  const validate = () => {
    const errors = {};
    const numericType = Number(type);
    const numericAmount = Number(amount);

    if (!movementTypes.some((movementType) => movementType.value === numericType)) {
      errors.type = messagesForField.type;
    }
    if (!Number.isFinite(numericAmount) || numericAmount <= 0) {
      errors.amount = messagesForField.amount;
    }
    if (!note.trim()) {
      errors.note = messagesForField.note;
    }

    setFieldErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const applyApiFieldErrors = (error) => {
    const errorMessage = error?.response?.data?.error_message || "";
    const fields = errorMessage.match(/fields=([a-z,]+)/)?.[1]?.split(",") || [];
    const errors = fields.reduce((current, field) => {
      if (MOVEMENT_FIELD_NAMES.includes(field)) {
        current[field] = messagesForField[field];
      }
      return current;
    }, {});

    if (Object.keys(errors).length > 0) {
      setFieldErrors(errors);
      return true;
    }
    return false;
  };

  const handleSubmit = async () => {
    if (!validate()) return;

    try {
      await insertMovement({
        locationId: Number(locationId),
        terminalCode: terminalId,
        type: Number(type),
        amount: Number(amount),
        note,
        ...(currency !== DEFAULT_CURRENCY ? { currency } : {}),
      });

      onSuccess?.();
      onClose();
    } catch (err) {
      console.error(err);
      if (!applyApiFieldErrors(err)) {
        alert(err?.response?.data?.error_message || t("cash_movement_save_error"));
      }
    }
  };


  return (
    <Modal open={open} onClose={onClose}>
      <Box sx={{ width: 400, bgcolor: "white", p: 4, m: "auto", mt: "10%", borderRadius: 2 }}>
        <Typography variant="h6" gutterBottom>{t("add_cash_movement")}</Typography>

        <Stack spacing={2}>
          <TextField
            select
            label={t("movement_type")}
            value={type}
            onChange={(e) => {
              setType(e.target.value);
              clearFieldError("type");
            }}
            error={!!fieldErrors.type}
            helperText={fieldErrors.type}
            required
            fullWidth
          >
            {movementTypes.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {t(option.labelKey)}
              </MenuItem>
            ))}
          </TextField>

          <CurrencySelect label="Moneda" value={currency} onChange={setCurrency} size="medium" />

          <TextField
            label={t("amount")}
            type="number"
            value={amount}
            onChange={(e) => {
              setAmount(e.target.value);
              clearFieldError("amount");
            }}
            inputProps={{ min: 0, step: "any" }}
            error={!!fieldErrors.amount}
            helperText={fieldErrors.amount}
            required
            fullWidth
          />

          <TextField
            label={isSupplyPurchase ? t("cash_movement_supply_invoice_placeholder") : t("note")}
            value={note}
            onChange={(e) => {
              setNote(e.target.value);
              clearFieldError("note");
            }}
            placeholder={isSupplyPurchase ? t("cash_movement_supply_invoice_placeholder") : ""}
            error={!!fieldErrors.note}
            helperText={fieldErrors.note}
            required
            multiline
            rows={3}
            fullWidth
          />

          <Button onClick={handleSubmit} variant="contained" color="primary">
            {t("save")}
          </Button>
        </Stack>
      </Box>
    </Modal>
  );
};

export default CashRegisterMovementModal;
